import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { PatientBackgroundFormField } from 'components/Assessments/PatAssessment/sections/PatientBackground/formField'
import { CodingFieldAdapter } from 'components/Assessments/PatAssessment/adapters/CodingFieldAdapter'

/**
 * History and Diagnoses subsection adapter
 * Handles diagnoses, therapies, equipment, and yes/no questions with notes
 */
export class HistoryAndDiagnosesAdapter extends CodingFieldAdapter {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract single Coding value using helper method
    // Preserve case for diagnosis codes (canonical values like ICD-10 codes)
    const primaryDiagnoses = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_PRIMARY_DIAGNOSES,
      false // normalizeCode = false to preserve canonical diagnosis codes
    )

    // Extract Coding arrays using helper method
    // Preserve case for diagnosis codes (canonical values like ICD-10 codes)
    const otherDiagnoses = this.extractCodingArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_OTHER_DIAGNOSES,
      false // normalizeCode = false to preserve canonical diagnosis codes
    )

    const therapies = this.extractTherapyArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_THERAPIES
    )

    const equipment = this.extractEquipmentArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_EQUIPMENT
    )

    const pcp = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_PCP,
      false
    )

    const equipmentOther = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_EQUIPMENT_OTHER
    )

    // Extract yes/no questions using helper method
    const erQuestion = this.extractYesNoQuestion(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_ER_QUESTION
    )

    const erNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_ER_NOTES
    )

    const hospitalQuestion = this.extractYesNoQuestion(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_HOSPITAL_QUESTION
    )

    const hospitalNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_HOSPITAL_NOTES
    )

    const injuriesQuestion = this.extractYesNoQuestion(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INJURIES_QUESTION
    )

    const injuriesNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INJURIES_NOTES
    )

    const fallsQuestion = this.extractYesNoQuestion(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_FALLS_QUESTION
    )

    const fallsNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_FALLS_NOTES
    )

    const infectionsQuestion = this.extractYesNoQuestion(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INFECTIONS_QUESTION
    )

    const infectionsNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INFECTIONS_NOTES
    )

    const mdQuestion = this.extractYesNoQuestion(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_MD_QUESTION
    )

    const mdNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_MD_NOTES
    )

    return {
      [PatientBackgroundFormField.PRIMARY_DIAGNOSES]: primaryDiagnoses,
      [PatientBackgroundFormField.OTHER_DIAGNOSES]: otherDiagnoses,
      [PatientBackgroundFormField.THERAPIES]: therapies,
      [PatientBackgroundFormField.EQUIPMENT]: equipment,
      [PatientBackgroundFormField.EQUIPMENT_OTHER]: equipmentOther,
      [PatientBackgroundFormField.PCP]: pcp,
      [PatientBackgroundFormField.ER_QUESTION]: erQuestion,
      [PatientBackgroundFormField.ER_NOTES]: erNotes,
      [PatientBackgroundFormField.HOSPITAL_QUESTION]: hospitalQuestion,
      [PatientBackgroundFormField.HOSPITAL_NOTES]: hospitalNotes,
      [PatientBackgroundFormField.INJURIES_QUESTION]: injuriesQuestion,
      [PatientBackgroundFormField.INJURIES_NOTES]: injuriesNotes,
      [PatientBackgroundFormField.FALLS_QUESTION]: fallsQuestion,
      [PatientBackgroundFormField.FALLS_NOTES]: fallsNotes,
      [PatientBackgroundFormField.INFECTIONS_QUESTION]: infectionsQuestion,
      [PatientBackgroundFormField.INFECTIONS_NOTES]: infectionsNotes,
      [PatientBackgroundFormField.MD_QUESTION]: mdQuestion,
      [PatientBackgroundFormField.MD_NOTES]: mdNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build single Coding value mutation using helper method
    // Preserve case for diagnosis codes (canonical values like ICD-10 codes)
    const primaryDiagnosesMutation = this.buildSingleCodingValueMutation(
      PatientBackgroundFormField.PRIMARY_DIAGNOSES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_PRIMARY_DIAGNOSES,
      false // normalizeCode = false to preserve canonical diagnosis codes
    )
    if (primaryDiagnosesMutation) {
      mutations.push(primaryDiagnosesMutation)
    }

    // Build Coding array mutations using helper method
    const otherDiagnosesMutation = this.buildCodingArrayMutation(
      PatientBackgroundFormField.OTHER_DIAGNOSES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_OTHER_DIAGNOSES,
      false
    )
    if (otherDiagnosesMutation) {
      mutations.push(otherDiagnosesMutation)
    }

    const therapiesMutation = this.buildCodingArrayMutation(
      PatientBackgroundFormField.THERAPIES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_THERAPIES,
      false // normalizeCode = false to preserve "physical_therapy_pt" format (don't lowercase)
    )
    if (therapiesMutation) {
      mutations.push(therapiesMutation)
    }

    const equipmentMutation = this.buildCodingArrayMutation(
      PatientBackgroundFormField.EQUIPMENT,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_EQUIPMENT,
      false
    )
    if (equipmentMutation) {
      mutations.push(equipmentMutation)
    }

    const equipmentOtherMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.EQUIPMENT_OTHER,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_EQUIPMENT_OTHER
    )
    if (equipmentOtherMutation) {
      mutations.push(equipmentOtherMutation)
    }

    const pcpMutation = this.buildSingleCodingValueMutation(
      PatientBackgroundFormField.PCP,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_PCP,
      false
    )
    if (pcpMutation) {
      mutations.push(pcpMutation)
    }

    // Build yes/no question mutations using helper method
    const erQuestionMutation = this.buildYesNoQuestionMutation(
      PatientBackgroundFormField.ER_QUESTION,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_ER_QUESTION
    )
    if (erQuestionMutation) {
      mutations.push(erQuestionMutation)
    }

    const erNotesMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.ER_NOTES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_ER_NOTES
    )
    if (erNotesMutation) {
      mutations.push(erNotesMutation)
    }

    const hospitalQuestionMutation = this.buildYesNoQuestionMutation(
      PatientBackgroundFormField.HOSPITAL_QUESTION,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_HOSPITAL_QUESTION
    )
    if (hospitalQuestionMutation) {
      mutations.push(hospitalQuestionMutation)
    }

    const hospitalNotesMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.HOSPITAL_NOTES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_HOSPITAL_NOTES
    )
    if (hospitalNotesMutation) {
      mutations.push(hospitalNotesMutation)
    }

    const injuriesQuestionMutation = this.buildYesNoQuestionMutation(
      PatientBackgroundFormField.INJURIES_QUESTION,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INJURIES_QUESTION
    )
    if (injuriesQuestionMutation) {
      mutations.push(injuriesQuestionMutation)
    }

    const injuriesNotesMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.INJURIES_NOTES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INJURIES_NOTES
    )
    if (injuriesNotesMutation) {
      mutations.push(injuriesNotesMutation)
    }

    const fallsQuestionMutation = this.buildYesNoQuestionMutation(
      PatientBackgroundFormField.FALLS_QUESTION,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_FALLS_QUESTION
    )
    if (fallsQuestionMutation) {
      mutations.push(fallsQuestionMutation)
    }

    const fallsNotesMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.FALLS_NOTES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_FALLS_NOTES
    )
    if (fallsNotesMutation) {
      mutations.push(fallsNotesMutation)
    }

    const infectionsQuestionMutation = this.buildYesNoQuestionMutation(
      PatientBackgroundFormField.INFECTIONS_QUESTION,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INFECTIONS_QUESTION
    )
    if (infectionsQuestionMutation) {
      mutations.push(infectionsQuestionMutation)
    }

    const infectionsNotesMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.INFECTIONS_NOTES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_INFECTIONS_NOTES
    )
    if (infectionsNotesMutation) {
      mutations.push(infectionsNotesMutation)
    }

    const mdQuestionMutation = this.buildYesNoQuestionMutation(
      PatientBackgroundFormField.MD_QUESTION,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_MD_QUESTION
    )
    if (mdQuestionMutation) {
      mutations.push(mdQuestionMutation)
    }

    const mdNotesMutation = this.buildNotesFieldMutation(
      PatientBackgroundFormField.MD_NOTES,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_HISTORY_AND_DIAGNOSES_MD_NOTES
    )
    if (mdNotesMutation) {
      mutations.push(mdNotesMutation)
    }

    return mutations
  }
}
